/**
 * taxOutputGuardRules — the whole brain of the "ตรวจก่อนส่ง (Guard)" rule table, kept OUT of the SFC so it
 * can be unit-tested under plain Node (see tests/taxOutputGuardRules.test.mjs).
 * `components/TaxOutputGuardRulesEditor.vue` imports it; nothing else should need to.
 *
 * The document it serves is `tax.TaxPlanningSettingsV2.OutputGuardRulesJson` — the ordered list of repairs the
 * node runs over EVERY outgoing message right before the customer sees it (`BAL/BAL/AppBAL/LHB/TaxOutputGuardV2.cs`).
 * An admin used to hand-author that array as raw JSON in a textarea, which asked them to get two unrelated
 * grammars right at once: JSON escaping and .NET regex. Typing a pattern such as the one that repairs the
 * bubble separator means typing every backslash twice, and getting it wrong produces a rule that silently
 * never fires.
 *
 * 🔴 RULE 1 — THE TABLE HOLDS RAW REGEX, THE FILE HOLDS JSON. Everything here works on rows whose `find` is the
 * pattern the author actually means (`<\s*…`), and `JSON.stringify` does the escaping on the way out. That single
 * translation is the reason the editor exists, so no helper here may ever pre-escape or "fix up" a pattern.
 *
 * 🔴 RULE 2 — BLANK, `[]`, AND A LIST ARE THREE DIFFERENT ANSWERS. Blank means "no override, run the built-in
 * set"; `[]` means "override with nothing — the guard still runs, it just has no rules"; a list means "run
 * exactly this". `parseRules` reports blank separately from an empty array for exactly that reason, and
 * `rulesEqual` refuses to call them equal. Collapsing the two would either write an override the admin never
 * asked for (and break the `config:` vs `nodeDefault` provenance the Playground shows) or quietly re-enable six
 * rules the admin had just switched off.
 *
 * 🔴 RULE 3 — `validateRules` MIRRORS `TaxOutputGuardV2.ValidateRules` (~:266-330), which is what actually
 * refuses a save. It exists to say the same "no" in Thai, in place, before the round trip — never to say a
 * DIFFERENT no. The one check deliberately NOT mirrored is regex compilation: see `regexWarning`.
 *
 * Pure logic (NO Vue / framework import) so it runs in plain Node.
 */

/** Ceilings copied from the engine (`TaxOutputGuardV2.MaxRules` / `MaxPatternChars` / `MaxReplaceChars`).
 *  They are safety limits, not preferences — a set past them is refused at save time by the backend. */
export const MAX_RULES = 50;
export const MAX_PATTERN = 500;
export const MAX_REPLACE = 200;

/**
 * Every `op` the engine implements (`TaxOutputGuardV2.KnownOps`), with the Thai an admin reads.
 *
 * These are the repairs that are expressible as a regex but that nobody should have to WRITE as one: their
 * replacements contain literal newlines, which JSON escapes and a textarea hides. A rule naming an `op` outside
 * this list is skipped at run time and refused at save time — an op nobody implements is an admin who believes a
 * repair is happening when it is not.
 *
 * 🔴 The `brk*` ops all match the canonical `<brk/>` spelling EXACTLY (case-sensitive, no spaces), so they only
 * do useful work when they run AFTER the regex rules that drag the model's near-misses onto that spelling. Order
 * is the feature, which is why the editor shows the list as a numbered, reorderable run order.
 *
 * 🔴 `quickReplySyntax` is the one op whose subject is a MARKER rather than the prose (Fusion Note #550). It runs
 * on the whole message before the engine cuts it at the first marker — the only rule allowed to — and it is a
 * parser, not a pattern: it reads the chips and writes the canonical `|quickReply|[…]` line back, or removes the
 * marker when there are no buttons. Its position in the run order does not matter.
 */
export const KNOWN_OPS = [
  {
    value: 'brkSpacing',
    labelTh: 'บังคับให้ <brk/> อยู่บรรทัดของตัวเอง',
    hintTh: 'ช่องว่าง/บรรทัดที่ติดอยู่รอบ token ถูกแทนด้วยการขึ้นบรรทัดใหม่ทั้งหน้าและหลัง',
  },
  {
    value: 'brkCollapse',
    labelTh: '<brk/> ที่ติดกันหลายอัน เหลืออันเดียว',
    hintTh: 'token สองอันติดกัน = มีฟองแชทเปล่าคั่นกลาง ลูกค้าเห็นเหมือนบอทพูดแล้วเงียบไปหนึ่งฟอง',
  },
  {
    value: 'brkTrimEdges',
    labelTh: '<brk/> ค้างหัว/ท้ายข้อความ ตัดทิ้ง',
    hintTh: 'ไม่มีเนื้อความอีกฝั่ง = ฟองแชทเปล่า — นี่คือเคสจริงที่ทำให้ต้องมีตัวตรวจนี้',
  },
  {
    value: 'quickReplySyntax',
    labelTh: 'marker |quickReply| เพี้ยน เขียนใหม่ให้ตรงรูป',
    hintTh: 'array ว่าง (ไม่มีปุ่ม) ⇒ ไม่ส่ง marker · | ที่เกินมาท้าย ⇒ ตัด · marker 2 ใบในข้อความเดียว ⇒ เหลือใบสุดท้าย · '
      + 'มีข้อความต่อหลัง marker ⇒ ย้าย marker ไปท้ายสุด — ข้อความที่ถูกต้องอยู่แล้วจะไม่ถูกแตะ',
  },
];

/** `op` → its Thai label, for showing a chosen builtin without re-scanning the list at the call site. */
export function opLabelTh(op) {
  const found = KNOWN_OPS.find((k) => k.value === text(op).trim());
  return found ? found.labelTh : '';
}

/** Every value read out of a parsed document goes through this — a rule file is hand-authored (and can arrive
 *  from a bundle import), so a number or `null` where a string belongs is a normal Tuesday, not an exception. */
function text(value) {
  return value === null || value === undefined ? '' : String(value);
}

/** Is this row a builtin? `kind` is what the editor's "ชนิด" picker writes; a row parsed from a file has no
 *  `kind` yet, so the presence of `op` decides — the same test the engine makes. */
function isOpRule(row) {
  if (!row) return false;
  if (row.kind === 'op') return true;
  if (row.kind === 'regex') return false;
  return Boolean(text(row.op).trim());
}

/**
 * One empty row for «เพิ่มกฎ». `id` is deliberately blank rather than auto-generated: it is the name that shows
 * up in the guard's trace line in the Flow tab, so a machine-made `rule-7` would be a name nobody recognises
 * when they later go looking for which rule mangled a message. `validateRules` asks for it immediately.
 */
export function blankRule() {
  return { id: '', enabled: true, kind: 'regex', op: '', find: '', replace: '', ignoreCase: false, note: '' };
}

/** One element of the stored array → the row shape the table edits. */
function toRow(el) {
  const op = text(el.op).trim();
  return {
    id: text(el.id),
    // Missing means true (`TaxOutputGuardV2` reads `enabled` with a default of true) — only an explicit
    // `false` switches a rule off, so a hand-written rule without the key still runs.
    enabled: el.enabled !== false,
    kind: op ? 'op' : 'regex',
    op,
    // 🔴 NOT trimmed. A leading/trailing space is a real part of a pattern and of a replacement
    // (`" <brk/> "` is a different repair from `"<brk/>"`), and this editor's whole job is to hand the
    // author's pattern back to them unchanged.
    find: text(el.find),
    replace: text(el.replace),
    ignoreCase: el.ignoreCase === true,
    note: text(el.note),
  };
}

/**
 * The stored JSON → rows the table can edit.
 *
 * @param {*} rawText The column's current value.
 * @returns {{rows: Array, error: string, blank: boolean}} `blank` is the "ยังไม่ override — ใช้ชุดเริ่มต้นในโค้ด"
 *   state and is NOT the same as `rows: []` from a literal `[]` (see RULE 2). `error` is a Thai sentence when
 *   the document cannot be read at all; it never throws, because mid-edit is the normal state of these boxes.
 */
export function parseRules(rawText) {
  const raw = text(rawText);
  if (!raw.trim()) return { rows: [], error: '', blank: true };

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    return { rows: [], error: `รูปแบบ JSON ไม่ถูกต้อง — ${err.message}`, blank: false };
  }
  if (!Array.isArray(parsed)) {
    return { rows: [], error: 'กติกาต้องเป็น JSON array (เช่น [] หรือ [{"id":"…"}])', blank: false };
  }

  const rows = [];
  for (let i = 0; i < parsed.length; i += 1) {
    const el = parsed[i];
    if (!el || typeof el !== 'object' || Array.isArray(el)) {
      return { rows: [], error: `กฎลำดับที่ ${i + 1}: ต้องเป็น object`, blank: false };
    }
    rows.push(toRow(el));
  }
  return { rows, error: '', blank: false };
}

/**
 * Rows → the JSON string stored in the column.
 *
 * 🔴 Only the keys that belong to the row's KIND are written: a regex rule carries no `op`, a builtin carries no
 * `find`/`replace`/`ignoreCase`. The editor keeps both sets in memory so that flipping the "ชนิด" picker back
 * and forth does not lose what was typed, but a rule that could be read two ways is a rule whose behaviour
 * depends on the reader — the engine takes `op` first, and a leftover `find` beside it would look like a repair
 * that never runs.
 *
 * `enabled` is always written (an admin reading the raw file should see the switch, not infer it), `note` only
 * when it says something, and `ignoreCase` only when true — all three round-trip through `parseRules` unchanged.
 */
export function serializeRules(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const out = list.map((row) => {
    const r = row || {};
    const rule = { id: text(r.id).trim(), enabled: r.enabled !== false };
    if (isOpRule(r)) {
      rule.op = text(r.op).trim();
    } else {
      rule.find = text(r.find);
      rule.replace = text(r.replace);
      if (r.ignoreCase === true) rule.ignoreCase = true;
    }
    const note = text(r.note).trim();
    if (note) rule.note = note;
    return rule;
  });
  return JSON.stringify(out, null, 2);
}

/**
 * Everything wrong with a set of rows, as `{ index, field, messageTh }` — mirroring
 * `TaxOutputGuardV2.ValidateRules`, which is the check that actually refuses the save.
 *
 * 🔴 `index: -1` means the problem is with the SET, not a row (only the rule-count ceiling today) — the editor
 * shows those above the table, where there is no row to attach them to.
 *
 * Unlike the backend this reports EVERY problem rather than the first: the backend is answering "may I write
 * this row", where one reason is enough, and this is answering "what do I still have to fix", where stopping at
 * the first fault turns one screen of work into six round trips.
 *
 * @param {Array} rows
 * @returns {Array<{index: number, field: string, messageTh: string}>}
 */
export function validateRules(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const out = [];

  if (list.length > MAX_RULES) {
    out.push({ index: -1, field: 'rules', messageTh: `กฎเกิน ${MAX_RULES} ข้อ (ตอนนี้ ${list.length} ข้อ) — ลบออกก่อนจึงจะบันทึกได้` });
  }

  const seen = new Map();
  list.forEach((row, index) => {
    const r = row || {};

    const id = text(r.id).trim();
    if (!id) {
      out.push({ index, field: 'id', messageTh: 'ต้องตั้งชื่อกฎ (id) — เป็นชื่อที่โผล่ในบรรทัดตรวจสอบของแท็บ Flow' });
    } else {
      // Case-insensitive, exactly like the backend's `HashSet<string>(StringComparer.OrdinalIgnoreCase)`:
      // two rules called `brk.tag` and `BRK.Tag` are one name as far as any trace line is concerned.
      const key = id.toLowerCase();
      if (seen.has(key)) {
        out.push({ index, field: 'id', messageTh: `id "${id}" ซ้ำกับกฎลำดับที่ ${seen.get(key) + 1}` });
      } else {
        seen.set(key, index);
      }
    }

    if (isOpRule(r)) {
      const op = text(r.op).trim();
      if (!op) {
        out.push({ index, field: 'op', messageTh: 'เลือกด้วยว่ากฎสำเร็จรูปนี้ทำอะไร' });
      } else if (!KNOWN_OPS.some((k) => k.value === op)) {
        out.push({
          index,
          field: 'op',
          messageTh: `กฎสำเร็จรูป "${op}" ไม่รู้จัก (รองรับ ${KNOWN_OPS.map((k) => k.value).join(' / ')})`,
        });
      }
      return;
    }

    // 🔴 Empty, not whitespace — the backend tests `string.IsNullOrEmpty(find)`, so a pattern of one space is a
    //    legal (and meaningful) rule and must not be rejected here.
    const find = text(r.find);
    if (!find) {
      out.push({ index, field: 'find', messageTh: 'ต้องใส่ข้อความค้นหา (regex) หรือเปลี่ยนชนิดเป็นกฎสำเร็จรูป' });
    } else if (find.length > MAX_PATTERN) {
      out.push({ index, field: 'find', messageTh: `ข้อความค้นหายาวเกิน ${MAX_PATTERN} ตัวอักษร (ตอนนี้ ${find.length})` });
    }

    const replace = text(r.replace);
    if (replace.length > MAX_REPLACE) {
      out.push({ index, field: 'replace', messageTh: `ข้อความแทนที่ยาวเกิน ${MAX_REPLACE} ตัวอักษร (ตอนนี้ ${replace.length})` });
    }
  });

  return out;
}

/** Appended to every regex complaint. It is the whole reason this is a warning. */
const ENGINE_NOTE =
  'ตรวจด้วยเอนจินของเบราว์เซอร์ ฝั่งเซิร์ฟเวอร์เป็น .NET อาจต่างกันเล็กน้อย';

/**
 * A pattern the browser cannot compile, reported as a WARNING — never an error, and never something that blocks
 * a save.
 *
 * 🔴 The two engines really do disagree: .NET has constructs JavaScript's `RegExp` rejects outright (balancing
 * groups `(?<a-b>…)`, `\p{IsThai}`, inline `(?i:…)` in older runtimes, conditionals), so a pattern that is
 * perfectly valid on the server can throw here. Blocking on that would make this editor refuse rules the engine
 * would happily run. And it is safe to be lax, because `TaxOutputGuardV2.ValidateRules` compiles every pattern
 * with the REAL engine at save time and returns a 400 — the authoritative "no" already exists.
 *
 * So this catches the common typo (an unclosed bracket, a stray quantifier) early, and says out loud that it
 * might be wrong about it.
 *
 * @param {*} pattern
 * @returns {?string} Thai warning text, or null when there is nothing to say.
 */
export function regexWarning(pattern) {
  const p = text(pattern);
  if (!p) return null;
  try {
    RegExp(p);
    return null;
  } catch (err) {
    return `regex อาจไม่ถูกต้อง — ${err.message} · ${ENGINE_NOTE} · บันทึกได้ ระบบจะตรวจอีกชั้นตอนบันทึก`;
  }
}

/**
 * Do these two documents say the same thing? SEMANTIC comparison — parse both and compare the content, so
 * indentation, key order and `"ignoreCase": false` written out longhand do not count as an edit.
 *
 * 🔴 This is what stops the editor writing to the column just because it re-printed what it read. The dialog's
 * "ยังไม่ได้บันทึก" flag is a raw string compare of a snapshot taken at load (`TaxPlanningSettingsV2Dialog.vue`
 * `snapshot`), so an editor that normalised the stored text on mount would raise that flag every single time the
 * dialog was opened, on a version nobody had touched.
 *
 * Order of the rules IS content (rule N runs on the output of rule N-1), so the comparison keeps it.
 * Blank vs `[]` are NOT equal (RULE 2). Two documents that both fail to parse are compared as raw text, which is
 * the only honest answer available for them.
 */
export function rulesEqual(aText, bText) {
  const a = parseRules(aText);
  const b = parseRules(bText);
  if (a.blank || b.blank) return a.blank && b.blank;
  if (a.error || b.error) return text(aText) === text(bText);
  return serializeRules(a.rows) === serializeRules(b.rows);
}
